import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import shopProductSelectors from "../../../redux/shop/products/selectors";
import { Button, Col, Row } from "reactstrap";
import Select from "react-select";

const ProductVariantSelector = (
  { changeProductOption, productId, currentVariant, selectedOptions },
  context
) => {
  //Fetch the language from the Redux store
  const language = useSelector((state) => state.i18nState.lang);

  const shopProduct = useSelector((state) =>
    shopProductSelectors.selectProductById(state, language, productId)
  );

  const shopProductData = {
    attributes: shopProduct?.data?.attributes,
    relationships: shopProduct?.data?.relationships,
    included: shopProduct?.included,
  };

  if (!shopProductData.included || !shopProductData.relationships?.option_types)
    return null;

  const renderWarningAlert = () => {
    if (
      currentVariant &&
      !selectedOptions
        .map((opt) => opt.id)
        .every((id) =>
          currentVariant.relationships.option_values.data
            .map((opt) => opt.id)
            .includes(id)
        )
    ) {
      return (
        <div className="alert alert-warning fade show">
          {context.t(
            "Unfortunately you selected a not available combination of attributes. We preselected a combination that is close to your choice."
          )}
        </div>
      );
    }
    return null;
  };

  const renderOptionButtons = (optionData, p_option) => (
    <Col xs={12}>
      {optionData.relationships.option_values.data.map((v_option, idx) => {
        const variantData = shopProductData.included.find(
          (obj) =>
            obj.id === v_option.id &&
            obj.type === "option_value" &&
            obj.relationships.option_type.data.id === p_option.id
        );

        const selected = currentVariant.relationships.option_values.data.find(
          (obj) => obj.id === v_option.id && obj.type === "option_value"
        );

        return variantData ? (
          <Button
            key={`v_option-${idx}`}
            color="primary"
            outline={!selected}
            size="sm"
            onClick={() => changeProductOption(variantData)}
            className="mr-1 mb-1"
          >
            {variantData.attributes.presentation}
          </Button>
        ) : null;
      })}
    </Col>
  );

  const renderOptionsDropdown = (optionData, p_option) => {
    const selectOptions = optionData.relationships.option_values.data
      .filter((v_option) => {
        const variantData = shopProductData.included.find(
          (obj) =>
            obj.id === v_option.id &&
            obj.type === "option_value" &&
            obj.relationships.option_type.data.id === p_option.id
        );
        return !!variantData; // Only keep items with valid variantData
      })
      .map((v_option, idx) => {
        const variantData = shopProductData.included.find(
          (obj) =>
            obj.id === v_option.id &&
            obj.type === "option_value" &&
            obj.relationships.option_type.data.id === p_option.id
        );

        return {
          value: variantData,
          label: variantData.attributes.presentation,
          selected: !!currentVariant.relationships.option_values.data.find(
            (obj) => obj.id === v_option.id && obj.type === "option_value"
          ),
        };
      });

    return (
      <Col xs={12}>
        <Select
          placeholder={context.t("Please select")}
          defaultValue={
            selectOptions.find((selOpt) => {
              return selOpt.selected;
            }) || selectOptions[0]
          }
          options={selectOptions}
          onChange={(ev) => changeProductOption(ev.value)}
          isSearchable
        />
      </Col>
    );
  };

  return (
    <>
      {shopProductData.relationships.option_types.data.map((p_option) => {
        const optionData = shopProductData.included.find(
          (obj) =>
            obj.id === p_option.id &&
            obj.type === "option_type" &&
            obj.attributes.name !== "custom_size" &&
            obj.attributes.name !== "custom_colorselect"
        );

        if (!optionData) return null;

        return (
          <Row key={optionData.id} className="mb-2">
            <span className="h6 col-12">
              {optionData.attributes.presentation}
            </span>
            {optionData.relationships.option_values.data.filter((v_option) => {
              const variantData = shopProductData.included.find(
                (obj) =>
                  obj.id === v_option.id &&
                  obj.type === "option_value" &&
                  obj.relationships.option_type.data.id === p_option.id
              );
              return !!variantData; // Only keep items with valid variantData
            }).length > 6
              ? renderOptionsDropdown(optionData, p_option)
              : renderOptionButtons(optionData, p_option)}
          </Row>
        );
      })}
      {renderWarningAlert()}
    </>
  );
};

ProductVariantSelector.propTypes = {
  productId: PropTypes.string.isRequired,
  changeProductOption: PropTypes.func.isRequired,
  currentVariant: PropTypes.object,
  selectedOptions: PropTypes.array.isRequired,
};

/**  define proptype for the 'translation' function  */
ProductVariantSelector.contextTypes = {
  t: PropTypes.func,
};

export default ProductVariantSelector;
